import { useEffect, useState } from 'react';
import { EntityModal } from '../common/EntityModal';
import { SearchableSelect } from '../common/SearchableSelect';

interface DuplicatePlanModalProps {
  open: boolean;
  onClose: () => void;
  onDuplicate: (targetEventId: string, name: string | undefined) => void;
  isPending?: boolean;
}

/** Copy a floor plan into another event, optionally under a new name. */
export function DuplicatePlanModal({
  open,
  onClose,
  onDuplicate,
  isPending,
}: DuplicatePlanModalProps) {
  const [targetEventId, setTargetEventId] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) {
      setTargetEventId('');
      setName('');
    }
  }, [open]);

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Duplicate Floor Plan"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onDuplicate(targetEventId, name.trim() || undefined)}
            disabled={!targetEventId || isPending}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
            data-testid="duplicate-confirm-btn"
          >
            Duplicate
          </button>
        </div>
      }
    >
      <div className="space-y-3" data-testid="duplicate-modal">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Event</label>
          <SearchableSelect
            entityType="event"
            value={targetEventId}
            onChange={(v) => setTargetEventId(typeof v === 'string' ? v : '')}
            placeholder="Search event..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="duplicate-name">
            New Name (optional)
          </label>
          <input
            id="duplicate-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Leave blank to copy original name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="duplicate-name"
          />
        </div>
      </div>
    </EntityModal>
  );
}
